import React, { useEffect, useState, useRef } from 'react'
import { createStyles } from '@codeleap/styles'
import { theme } from '@/config'

interface WaveformProps {
  isRecording: boolean
  barCount?: number
}

export const Waveform: React.FC<WaveformProps> = ({ isRecording, barCount = 50 }) => {
  const [bars, setBars] = useState<number[]>(Array(barCount).fill(4))
  const animationFrameRef = useRef<number>()
  const startTimeRef = useRef<number>(Date.now())
  const barOffsetsRef = useRef<number[]>(
    Array(barCount).fill(0).map(() => Math.random() * Math.PI * 2)
  )

  useEffect(() => {
    // Always cleanup previous animation first
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = undefined
    }

    if (!isRecording) {
      setBars(Array(barCount).fill(4))
      return
    }

    startTimeRef.current = Date.now()

    let lastUpdate = Date.now()

    const animate = () => {
      const now = Date.now()

      if (now - lastUpdate > 50) {
        lastUpdate = now
        const elapsed = now - startTimeRef.current

        setBars(
          barOffsetsRef.current.map((offset, i) => {
            const baseHeight = 4
            const maxHeight = 20

            const wave1 = Math.sin((elapsed / 300) + offset)
            const wave2 = Math.sin((elapsed / 150) + offset * 2)
            const wave3 = Math.sin((elapsed / 450) + offset * 0.5)

            const combined = (wave1 + wave2 * 0.5 + wave3 * 0.3) / 1.8
            const randomVariation = Math.random() * 0.3
            const normalized = (combined + randomVariation + 1) / 2 // Normalize to 0-1

            return baseHeight + (maxHeight - baseHeight) * normalized
          })
        )
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = undefined
      }
    }
  }, [isRecording, barCount])

  return (
    <div style={styles.wrapper}>
      {bars.map((height, i) => (
        <div
          key={i}
          style={{
            ...styles.bar,
            height: `${height}px`,
          }}
        />
      ))}
    </div>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    flex: 1,
    maxWidth: 400,
    height: 24,
  },
  bar: {
    width: 2,
    background: '#9CA3AF',
    borderRadius: 1,
    transition: 'height 0.1s ease-out',
  },
}))
